import { inject, PLATFORM_ID, TransferState, makeStateKey } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { ResolveFn, ActivatedRouteSnapshot, Router } from '@angular/router';
import { Observable, switchMap, of, tap, EMPTY, from, map } from 'rxjs';
import { ServicioRoutingDinamico } from '../services/routing/routing-dinamico.service';
import type { PaginaConRuta } from '../services/routing/routing-dinamico.types';
import { ServicioContenidoModyo } from '@core/services/modyo/modyo-contenido.service';
import { ServicioExpansionReferenciasModyo } from '@core/services/modyo/expansion-referencias-modyo.service';
import { ServicioTransformadorModyo } from '@core/services/modyo/modyo-transformador.service';
import { cargarNgStateSiNecesario } from '@core/utils/cargar-ng-state-pagina.util';
import type { ModyoEntry } from '@core/interfaces/modyo/modyo-base.interface';
import type { ModyoEntryResponse } from '@core/interfaces/modyo-entry-response.interface';
import type { EstructuraPaginaModyo } from '@core/interfaces/pagina-modyo.interface';

export const resolucionPaginaDinamica: ResolveFn<EstructuraPaginaModyo> = (
  route: ActivatedRouteSnapshot,
): Observable<EstructuraPaginaModyo> => {
  const servicioRouting = inject(ServicioRoutingDinamico);
  const servicioModyo = inject(ServicioContenidoModyo);
  const expansion = inject(ServicioExpansionReferenciasModyo);
  const transformador = inject(ServicioTransformadorModyo);
  const transferState = inject(TransferState);
  const router = inject(Router);
  const esServidor = isPlatformServer(inject(PLATFORM_ID));

  const rutaCompleta = route.url.map((segment) => segment.path).join('/');
  const claveTransferState = makeStateKey<EstructuraPaginaModyo>(`pagina-dinamica-${rutaCompleta}`);

  function obtenerDesdeModyo(): Observable<EstructuraPaginaModyo> {
    return servicioRouting.obtenerPaginaPorRuta(rutaCompleta).pipe(
      switchMap((paginaInfo: PaginaConRuta | null) => {
        if (!paginaInfo) {
          void router.navigate(['/bienvenida']);
          return EMPTY;
        }
        return servicioModyo.obtenerEntradaPorSlug<unknown>('pagina', paginaInfo.slug);
      }),
      switchMap((pagina) => expansion.expandirEntry(pagina as ModyoEntry<unknown>)),
      map((expandida) => transformador.transformarPagina(expandida as unknown as ModyoEntryResponse)),
      tap((datos) => {
        if (esServidor) transferState.set(claveTransferState, datos);
      }),
    );
  }

  if (esServidor) return obtenerDesdeModyo();

  const enCache = transferState.get(claveTransferState, null);
  if (enCache) {
    transferState.remove(claveTransferState);
    return of(enCache);
  }

  return from(cargarNgStateSiNecesario(rutaCompleta, transferState)).pipe(
    switchMap(() => {
      const deSSG = transferState.get(claveTransferState, null);
      if (deSSG) {
        transferState.remove(claveTransferState);
        return of(deSSG);
      }
      return obtenerDesdeModyo();
    }),
  );
};
